import { useRef, useState } from "react";
import { motion, useMotionValue, useTransform, useMotionTemplate } from "framer-motion";

interface AnimatedMenuButtonProps {
  onClick: () => void;
}

const AnimatedMenuButton = ({ onClick }: AnimatedMenuButtonProps) => {
  const containerRef = useRef<HTMLButtonElement>(null);
  const mouseX = useMotionValue(50);
  const mouseY = useMotionValue(50);
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    mouseX.set(x);
    mouseY.set(y);
  };

  // Transform mouse position to line offsets
  const line1X = useTransform(mouseX, (x) => (x - 50) * 0.08);
  const line2X = useTransform(mouseX, (x) => (x - 50) * 0.12);
  const line3X = useTransform(mouseX, (x) => (x - 50) * 0.1);

  // Background gradient - always computed, visibility controlled by CSS
  const backgroundGradient = useMotionTemplate`radial-gradient(circle at ${mouseX}% ${mouseY}%, rgba(56, 159, 186, 0.15) 0%, transparent 60%)`;

  return (
    <button
      ref={containerRef}
      onClick={onClick}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="group relative flex items-center gap-3 py-2.5 px-4 bg-white rounded-full"
      aria-label="Open menu"
    >
      {/* Background glow that follows mouse */}
      <motion.div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-lg"
        style={{
          background: backgroundGradient,
        }}
      />

      {/* Menu lines container */}
      <div className="flex flex-col gap-[5px]">
        <motion.span
          className="h-[2px] bg-yacht-charcoal/70 group-hover:bg-yacht-charcoal transition-colors duration-300 ease-out"
          style={{
            width: 20,
            x: isHovered ? line1X : 0,
          }}
        />
        <motion.span
          className="h-[2px] bg-yacht-charcoal/70 group-hover:bg-yacht-charcoal transition-colors duration-300 ease-out"
          style={{
            width: 15,
            x: isHovered ? line2X : 0,
          }}
        />
        <motion.span
          className="h-[2px] bg-yacht-charcoal/70 group-hover:bg-yacht-charcoal transition-colors duration-300 ease-out"
          style={{
            width: 17,
            x: isHovered ? line3X : 0,
          }}
        />
      </div>

      {/* Menu text */}
      <span className="text-sm font-medium text-yacht-charcoal/80 group-hover:text-yacht-charcoal transition-colors tracking-wide uppercase">
        Menu
      </span>
    </button>
  );
};

export default AnimatedMenuButton;
